import type { FateCard } from '../../types';
import { useAutoConfirm } from '../../hooks/useAutoConfirm';
import styles from './EventModal.module.css';

interface Props {
  type: string;
  data: any;
  onClose: () => void;
}

/** 是否需要对弹窗做10秒自动确认（所有事件弹窗统一） */
const AUTO_CLOSE_TYPES = ['fate', 'cellEvent', 'comeback', 'landChange'];

export default function EventModal({ type, data, onClose }: Props) {
  const { countdown, cancelAutoConfirm } = useAutoConfirm({
    onAutoConfirm: onClose,
    delayMs: 10000,
    enabled: AUTO_CLOSE_TYPES.includes(type) && !!data,
  });

  const countdownDisplay = AUTO_CLOSE_TYPES.includes(type) && (
    <div className={styles.countdown}>⏰ {countdown}s 后自动确定</div>
  );

  // ====== 命运卡弹框 ======
  if (type === 'fate' && data) {
    const card = data as FateCard;
    const categoryColors = {
      good: '#4CAF50',
      bad: '#F44336',
      reverse: '#FF9800',
    };
    const categoryLabels = {
      good: '好运',
      bad: '厄运',
      reverse: '反转',
    };
    const color = categoryColors[card.category];
    const label = categoryLabels[card.category];

    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.cardVisual} style={{ borderColor: color }}>
            <div className={styles.cardGlow} style={{ background: `${color}20` }} />
            <span className={styles.cardCategory} style={{ color }}>{label}</span>
            <span className={styles.cardEmoji}>{card.emoji}</span>
            <h3 className={styles.cardTitle}>{card.name}</h3>
            <p className={styles.cardDesc}>{card.description}</p>
          </div>
          {countdownDisplay}
          <button className={styles.closeBtn} onClick={() => { cancelAutoConfirm(); onClose(); }} style={{ background: color }}>
            确定
          </button>
        </div>
      </div>
    );
  }

  // ====== 通用格子事件弹框 ======
  if (type === 'cellEvent' && data) {
    const { emoji, title, description, color } = data as { emoji: string; title: string; description: string; color?: string };
    const borderColor = color || '#6C5CE7';

    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.cardVisual} style={{ borderColor }}>
            <div className={styles.cardGlow} style={{ background: `${borderColor}20` }} />
            <span className={styles.cardEmoji} style={{ fontSize: 48 }}>{emoji}</span>
            <h3 className={styles.cardTitle}>{title}</h3>
            <p className={styles.cardDesc}>{description}</p>
          </div>
          {countdownDisplay}
          <button className={styles.closeBtn} onClick={() => { cancelAutoConfirm(); onClose(); }} style={{ background: borderColor }}>
            确定
          </button>
        </div>
      </div>
    );
  }

  // ====== 绝境反击弹框 ======
  if (type === 'comeback' && data) {
    const { emoji, title, description } = data as { emoji: string; title: string; description: string };
    const borderColor = '#FF6B35';

    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.cardVisual} style={{ borderColor }}>
            <div className={styles.cardGlow} style={{ background: `${borderColor}20` }} />
            <span className={styles.cardCategory} style={{ color: borderColor }}>绝境反击</span>
            <span className={styles.cardEmoji}>{emoji}</span>
            <h3 className={styles.cardTitle}>{title}</h3>
            <p className={styles.cardDesc}>{description}</p>
          </div>
          {countdownDisplay}
          <button className={styles.closeBtn} onClick={() => { cancelAutoConfirm(); onClose(); }} style={{ background: borderColor }}>
            知道了
          </button>
        </div>
      </div>
    );
  }

  // ====== 地产变动弹框（土地丢失/降级 → 确认后闪烁对应格子） ======
  if (type === 'landChange' && data) {
    const { cellName, message, landType } = data as { cellId: number; cellName: string; landType: string; message: string };
    const typeColors: Record<string, string> = {
      lost: '#F44336',
      downgraded: '#FF9800',
      stolen: '#E91E63',
      swapped: '#2196F3',
      bankrupt: '#9E9E9E',
    };
    const borderColor = typeColors[landType] || '#F44336';
    const typeEmoji: Record<string, string> = {
      lost: '💔',
      downgraded: '⬇️',
      stolen: '👻',
      swapped: '🔄',
      bankrupt: '💸',
    };
    const emoji = typeEmoji[landType] || '📋';

    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.cardVisual} style={{ borderColor }}>
            <div className={styles.cardGlow} style={{ background: `${borderColor}20` }} />
            <span className={styles.cardEmoji} style={{ fontSize: 48 }}>{emoji}</span>
            <h3 className={styles.cardTitle}>🏠 {cellName}</h3>
            <p className={styles.cardDesc}>{message}</p>
            <p className={styles.cardDesc} style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>
              确定后将在地图上闪烁高亮该地块
            </p>
          </div>
          {countdownDisplay}
          <button className={styles.closeBtn} onClick={() => { cancelAutoConfirm(); onClose(); }} style={{ background: borderColor }}>
            确定
          </button>
        </div>
      </div>
    );
  }

  return null;
}
